import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { ITechListData } from '../../types/type';
import { instance } from '../../api/api';
import { toApiError } from '../../utils/apiError';

interface TechDataState {
  selectedDriver: Partial<ITechListData> | null;
  selectedTechLists: any | null;
  AlltechLists: ITechListData[] | null;
  techLists: any[] | null;
  totalCount: number;
  totalPages: number;
  error: string | null;
  loading: boolean;
}

const initialState: TechDataState = {
  selectedDriver: null,
  selectedTechLists: null,
  AlltechLists: null,
  techLists: null,
  totalCount: 0,
  totalPages: 0,
  error: null,
  loading: false,
};

export const fetchTechLists = createAsyncThunk<ITechListData[], string>(
  'techdata/fetchTechLists',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await instance.get<ITechListData[]>(`/techlist/technik/${id}`);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? error.message ?? 'Unknown error');
    }
  },
);

export const fetchSelectedTechData = createAsyncThunk<Partial<ITechListData>, { id: string | number }>(
  'techdata/fetchTechData',
  async ({ id }, { rejectWithValue }) => {
    try {
      const { data } = await instance.get<Partial<ITechListData>>(`/techlist/${id}`);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? error.message ?? 'Unknown error');
    }
  },
);

export const updateUserTechData = createAsyncThunk<
  ITechListData,
  { id: string | number; updates: Partial<ITechListData> }
>('techdata/updateUserTechData', async ({ id, updates }, { dispatch, rejectWithValue }) => {
  try {
    const { data } = await instance.put<ITechListData>(`/techlist/${id}`, { ...updates });
    dispatch(fetchSelectedTechData({ id }));
    return data;
  } catch (error: any) {
    const apiError = toApiError(error, error?.message ?? 'Unknown error');
    return rejectWithValue({ message: apiError.message, status: apiError.status, code: apiError.code });
  }
});

const techDataSlice = createSlice({
  name: 'techdata',
  initialState,
  reducers: {
    setTech(state, action: PayloadAction<any>) {
      state.selectedDriver = action.payload;
    },
    setTechLists(state, action: PayloadAction<{ data: any[]; totalCount: number; totalPages: number }>) {
      state.techLists = action.payload.data;
      state.totalCount = action.payload.totalCount;
      state.totalPages = action.payload.totalPages;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTechLists.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTechLists.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.AlltechLists = action.payload;
      })
      .addCase(fetchTechLists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Ошибка при загрузке ТехЛистов';
      })
      .addCase(fetchSelectedTechData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSelectedTechData.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.selectedTechLists = action.payload;
      })
      .addCase(fetchSelectedTechData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Ошибка при загрузке ТехЛиста';
      })
      .addCase(updateUserTechData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserTechData.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.selectedDriver = { ...state.selectedDriver, ...action.payload };
      })
      .addCase(updateUserTechData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Ошибка при обновлении ТехЛиста';
      });
  },
});

export const { setTech, setTechLists } = techDataSlice.actions;
export default techDataSlice.reducer;
